import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { todayISO, fmtDay } from "@/utils/haccp";

export default function HaccpCleaning() {
  const { authAxios } = useAuth();
  const [day, setDay] = useState(todayISO());
  const [groups, setGroups] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get(`/haccp/cleanings/due?day=${day}`);
      setGroups(r.data.groups || []);
    } catch {
      toast.error("Errore caricamento pulizie");
    }
  }, [authAxios, day]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (task_id, done) => {
    try {
      await authAxios.post("/haccp/cleanings", { task_id, done, date: day });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-cleaning-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Pulizie HACCP" />

        <div className="flex items-end justify-between gap-2 mb-4 flex-wrap">
          <p className="text-zinc-400 text-xs max-w-sm">
            Spunta un&apos;attività per registrarla con la tua firma. Settimanali, mensili e annuali restano spuntate per tutto il periodo di riferimento.
          </p>
          <Input
            type="date"
            data-testid="cleaning-date-input"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="h-9 w-40 bg-[#0f0f11] border-white/10 text-white"
          />
        </div>

        {groups.map((g) => (
          <Card key={g.frequency} className="bg-[#18181b] border-white/10 mb-4" data-testid={`cleaning-group-${g.frequency}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white text-base font-semibold">{g.label}</h3>
                <span className="text-[11px] text-zinc-500">
                  {g.items.filter((i) => i.done).length}/{g.items.length} completate
                </span>
              </div>
              <div className="space-y-2">
                {g.items.map((it) => (
                  <button
                    key={it.task_id}
                    type="button"
                    data-testid={`cleaning-task-${it.task_id}`}
                    onClick={() => toggle(it.task_id, !it.done)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors ${
                      it.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 hover:bg-white/5"
                    }`}
                  >
                    <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${it.done ? "bg-emerald-500" : "border border-zinc-600"}`}>
                      {it.done && <Check className="w-3.5 h-3.5 text-black" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm ${it.done ? "text-emerald-300" : "text-zinc-300"}`}>{it.name}</span>
                      {it.done && (
                        <span className="block text-[11px] text-zinc-500">
                          {fmtDay(it.done_at)} · {it.signed_by?.name || "—"}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
                {!g.items.length && <p className="text-zinc-500 text-sm">Nessuna attività configurata.</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
